/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom, from } from 'rxjs';
import type { PDFPageProxy } from 'pdfjs-dist';
import { OcrService } from './ocr.service';

/**
 * What a file needs before its words can be searched.
 *
 * - `image` — the whole file is pixels; OCR reads it.
 * - `pdf` — decided page by page, because one PDF can hold both typed pages and
 *   scanned ones.
 * - `none` — Word, Excel, PowerPoint and plain text carry their text already,
 *   and Alfresco extracts it on its own. There is nothing for OCR to add.
 */
export type OcrKind = 'image' | 'pdf' | 'none';

export interface OcrProgress {
  page: number;
  pages: number;
}

export interface OcrOutcome {
  /** Pages the file has. */
  total: number;
  /** Pages that were looked at — fewer than `total` only when the file is over the limit. */
  checked: number;
  /** Pages indexed from what OCR read off the pixels. */
  read: number;
  /** Pages indexed from text the PDF already carried. */
  fromText: number;
}

/**
 * The image formats the engine decodes. Measured, not assumed: each was sent to
 * the sidecar's `/api/ocr/recognize` with the same page on it. PNG, JPG, BMP,
 * GIF and WebP came back read; TIFF and AVIF came back "could not decode" —
 * SkiaSharp, which the sidecar decodes with, has no reader for either.
 */
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'bmp', 'gif', 'webp'];

/**
 * Below this many letters a PDF page is treated as a scan. A scanned page
 * often still carries a stamped page number or a scanner's footer in its text
 * layer, and taking that as "this page has text" would leave the page itself
 * unread.
 */
const MIN_TEXT_CHARS = 30;

/**
 * Pages are drawn so their long side is about this many pixels. Around 2000px
 * an A4 page puts ordinary body text near 30px tall, which the engine reads
 * reliably; much larger only costs time.
 */
const TARGET_LONG_SIDE = 2000;

/**
 * Whether a PDF page's own text can be used instead of reading the page.
 *
 * Too little text means a scan, as above. But text that is there can still be
 * wrong: Thai PDFs are often written with fonts whose tone marks and upper
 * vowels have no Unicode mapping, or map to the private-use "floating vowel"
 * codes some Thai fonts use (U+F700 to U+F71A). pdf.js then hands back NULs or
 * those private codes where the marks belong. Found in testing, not guessed: a
 * Chrome-printed PDF came back as "เรื" + NUL + "อง" for "เรื่อง". Indexed like
 * that, every word carrying a tone mark would be unfindable, so such a page is
 * read with OCR instead, which reads what is drawn rather than what the font
 * claims.
 */
export function usableTextLayer(text: string): boolean {
  if (text.replace(/\s/g, '').length < MIN_TEXT_CHARS) {
    return false;
  }

  for (const character of text) {
    const code = character.charCodeAt(0);
    const unmapped = code === 0;
    const privateUse = code >= 0xe000 && code <= 0xf8ff;
    const undecodable = code === 0xfffd;
    if (unmapped || privateUse || undecodable) {
      return false;
    }
  }
  return true;
}

/**
 * Reads the words out of a file that has just been uploaded and puts them in
 * the OCR search index under that file's node.
 *
 * This runs in the browser because PDFs have to be turned into pictures first,
 * and the engine only reads pictures. pdf.js is already part of this
 * application — the document viewer draws with it — so drawing a page here
 * adds nothing to install. Its worker is copied to the site root by the build
 * (`app/project.json`).
 *
 * A PDF page that already carries usable text is indexed from that text
 * instead of being read again. It is faster and exact, and it still matters:
 * Alfresco does extract that text itself, but its analyser treats a Thai
 * sentence as a single word, so a word from the middle of a Thai phrase is not
 * found through Alfresco's own index. The OCR index is analysed for Thai.
 */
@Injectable({ providedIn: 'root' })
export class OcrPipelineService {
  /**
   * The most pages one file is read for here. Every page is drawn and sent
   * from this browser tab, so a very long scan would hold the tab for many
   * minutes; past this the reader is told how many pages were read.
   */
  static readonly MAX_PAGES = 100;

  private readonly ocr = inject(OcrService);

  kindOf(name: string): OcrKind {
    const extension = (name.split('.').pop() ?? '').toLowerCase();
    if (extension === 'pdf') {
      return 'pdf';
    }
    return IMAGE_EXTENSIONS.includes(extension) ? 'image' : 'none';
  }

  /**
   * Reads the file and indexes what it says. Reports each page as it starts, so
   * a long scan does not look stalled. Errors are passed on for the caller to
   * describe — by the time this runs, the file is already in the repository.
   */
  readAndIndex(file: File, nodeId: string, name: string, progress: (progress: OcrProgress) => void): Observable<OcrOutcome> {
    return from(this.kindOf(name) === 'pdf' ? this.readPdf(file, nodeId, name, progress) : this.readImage(file, nodeId, name, progress));
  }

  /**
   * The text of one file, read but not indexed — for comparing against a
   * version that is not the current one, whose words the index never held.
   * Empty when the file's kind has no text the engine can reach (a Word or
   * Excel file, or a scan the engine cannot read).
   */
  async textOf(file: File, name: string, maxPages = 40): Promise<string> {
    const kind = this.kindOf(name);
    if (kind === 'image') {
      const result = await firstValueFrom(this.ocr.recognize(file));
      return result.text?.trim() ?? '';
    }
    if (kind === 'none') {
      return file.type.startsWith('text/') ? (await file.text()).trim() : '';
    }

    const pdfjs = await import('pdfjs-dist');
    pdfjs.GlobalWorkerOptions.workerSrc = `./pdf.worker.min.mjs?v=${pdfjs.version}`;

    const pdf = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
    const parts: string[] = [];
    try {
      for (let number = 1; number <= Math.min(pdf.numPages, maxPages); number++) {
        const page = await pdf.getPage(number);
        try {
          const text = await this.textLayerOf(page);
          if (usableTextLayer(text)) {
            parts.push(text);
            continue;
          }

          const image = await this.rasterise(page);
          const result = await firstValueFrom(this.ocr.recognize(new File([image], `${name}-${number}.png`, { type: 'image/png' })));
          if (result.text?.trim()) {
            parts.push(result.text.trim());
          }
        } finally {
          page.cleanup();
        }
      }
    } finally {
      await pdf.destroy();
    }
    return parts.join('\n\n').trim();
  }

  private async readImage(file: File, nodeId: string, name: string, progress: (progress: OcrProgress) => void): Promise<OcrOutcome> {
    progress({ page: 1, pages: 1 });
    const read = await this.recognise(file, nodeId, name, 1);
    return { total: 1, checked: 1, read: read ? 1 : 0, fromText: 0 };
  }

  private async readPdf(file: File, nodeId: string, name: string, progress: (progress: OcrProgress) => void): Promise<OcrOutcome> {
    // Loaded only when a PDF is actually read, so the upload page does not
    // carry pdf.js for people who never upload one.
    const pdfjs = await import('pdfjs-dist');
    pdfjs.GlobalWorkerOptions.workerSrc = `./pdf.worker.min.mjs?v=${pdfjs.version}`;

    const pdf = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
    const outcome: OcrOutcome = {
      total: pdf.numPages,
      checked: Math.min(pdf.numPages, OcrPipelineService.MAX_PAGES),
      read: 0,
      fromText: 0
    };

    try {
      for (let number = 1; number <= outcome.checked; number++) {
        progress({ page: number, pages: outcome.checked });
        const page = await pdf.getPage(number);
        try {
          const text = await this.textLayerOf(page);
          if (usableTextLayer(text)) {
            await firstValueFrom(this.ocr.indexText(nodeId, name, number, text, 'pdf text layer'));
            outcome.fromText++;
          } else if (await this.recognise(await this.rasterise(page), nodeId, name, number)) {
            outcome.read++;
          }
        } finally {
          page.cleanup();
        }
      }
    } finally {
      await pdf.destroy();
    }

    return outcome;
  }

  /** Sends one picture to the engine and indexes what comes back. A blank page is not an error. */
  private async recognise(image: Blob, nodeId: string, name: string, page: number): Promise<boolean> {
    const file = image instanceof File ? image : new File([image], `${name}-${page}.png`, { type: 'image/png' });
    const result = await firstValueFrom(this.ocr.recognize(file));
    if (!result.text?.trim()) {
      return false;
    }

    await firstValueFrom(this.ocr.indexOcrText(nodeId, name, result, page));
    return true;
  }

  private async textLayerOf(page: PDFPageProxy): Promise<string> {
    const content = await page.getTextContent();
    return content.items
      .map((item) => ('str' in item ? item.str + (item.hasEOL ? '\n' : '') : ''))
      .join('')
      .trim();
  }

  /** Draws a page on a white ground — the engine reads a transparent one as black. */
  private async rasterise(page: PDFPageProxy): Promise<Blob> {
    const natural = page.getViewport({ scale: 1 });
    const scale = Math.min(3, Math.max(1, TARGET_LONG_SIDE / Math.max(natural.width, natural.height)));
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('This browser cannot draw the page to read it.');
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, viewport }).promise;

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    // a drawn A4 page holds ~16 MB of pixels; give it back before the next one
    canvas.width = 0;
    canvas.height = 0;

    if (!blob) {
      throw new Error('The page could not be turned into a picture.');
    }
    return blob;
  }
}
